import { addDays, addMinutes, addMonths, set } from "date-fns";
import {
  BookingData,
  BookingTool,
  ServiceDuration,
  TimeSlot,
} from "../../../AppDataProvider/AppDataProvider";
import {
  getAvailabilityPartsInput,
  requireBookingDataFields,
  today,
  toZonedISOString,
} from "../../../utils";
import {
  GetOrganisationAvailabilityIntervalsQueryVariables,
  useGetOrganisationAvailabilityIntervalsQuery,
} from "../../../GeneratedGraphQL";

import { ApolloError } from "@apollo/client";
import { PartRequired } from "../../../utils/types";

type Range = {
  end: string;
  start: string;
};

export type UseGetTimeSlotsOpts = {
  attendees: number;
  bookingData: BookingData;
  bookingTool: BookingTool;
  duration: ServiceDuration;
  ranges?: Range[];
  requestRangesIndividually?: boolean;
  selectedDate: Date;
};

type GetMoreAvailabilityOpts = {
  monthOffset: number;
  setMonthOffset: (monthOffset: number) => void;
};

type GetMoreAvailabilityResult = {
  intervalCount: number;
};

export type UseGetTimeSlotsResult = {
  error?: ApolloError;
  getMoreAvailability: (
    options: GetMoreAvailabilityOpts,
  ) => Promise<GetMoreAvailabilityResult>;
  loading: boolean;
  timeSlots: TimeSlot[];
};

type UseGetTimeSlotsBookingData = PartRequired<
  BookingData,
  "organisation" | "service" | "sessionId" | "timezone"
>;

export const useGetTimeSlots = (
  options: UseGetTimeSlotsOpts,
): UseGetTimeSlotsResult => {
  const {
    attendees,
    selectedDate,
    bookingData,
    bookingTool,
    duration,
    ranges,
    requestRangesIndividually,
  } = options;

  requireBookingDataFields<UseGetTimeSlotsBookingData>(bookingData, [
    "service",
    "organisation",
    "sessionId",
    "timezone",
  ]);

  const {
    organisation,
    service,
    resource,
    resourceGroupId,
    timezone,
    sessionId,
  } = bookingData;
  const { parts } = service;
  const partsInput = getAvailabilityPartsInput(parts);

  const startIntervalsDate = set(selectedDate, {
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  let endIntervalsDate = addDays(startIntervalsDate, 1);

  /**
   * If the duration of a service is greater than 24hrs, it won't be
   * possible to see the availability unless the range is at least
   * as long as this duration.
   *
   * Extend the availability query to the length of the duration. This
   * query should return at most one interval.
   */
  if (duration.minutes > 1440) {
    endIntervalsDate = addMinutes(endIntervalsDate, duration.minutes - 1440);
  }

  /**
   * If we have been provided a resourceId in the url params,
   * we want to automatically filter availability by that resource id.
   */
  const urlParams = new URLSearchParams(window.location.search);
  const preFillResourceId = urlParams.get("resourceId");

  const resourceId = resource?._id ?? preFillResourceId;

  const variables: GetOrganisationAvailabilityIntervalsQueryVariables = {
    ...(!bookingTool.isGenericBookingTool && {
      bookingToolId: bookingTool._id,
    }),
    duration: duration.minutes,
    id: organisation._id,
    meta: { sessionId },
    ...(partsInput.length > 0 && { parts: partsInput }),
    serviceId: service._id,
    ...(resourceId && { resourceIds: [resourceId] }),
    ...(resourceGroupId && { resourceGroupIds: [resourceGroupId] }),
    ranges: ranges ?? [
      {
        end: toZonedISOString(endIntervalsDate, timezone),
        start: toZonedISOString(startIntervalsDate, timezone),
      },
    ],
    spaces: attendees,
    timezone,
    ...(requestRangesIndividually && { requestRangesIndividually }),
  };

  const { data, loading, error, fetchMore } =
    useGetOrganisationAvailabilityIntervalsQuery({
      errorPolicy: "all",
      fetchPolicy: "network-only",
      nextFetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      variables,
    });

  const timeSlots: TimeSlot[] =
    (data &&
      data.organisation?.availabilityIntervals &&
      data.organisation?.availabilityIntervals.map((interval) => {
        if (interval.__typename === "GroupBookingInterval") {
          return {
            /**
             * We need to manually map the interval in the case of
             * GroupBookingInterval. This is because the TS type of Booking
             * we reference in `types.ts` is a subset Pick<> of the type
             * associated to the Booking Graph.
             *
             * The Booking type associated to the TimeSlot type is intentionally
             * missing properties from the auto generated GraphQL.Booking.
             */
            interval: {
              __typename: interval.__typename,
              booking: {
                _id: interval.booking._id,
                end: interval.booking.end,
                remainingSpaces: interval.booking.remainingSpaces,
                start: interval.booking.start,
                ...(interval.booking.recurring && {
                  recurring: interval.booking.recurring,
                }),
                ...(interval.booking.price && {
                  price: interval.booking.price,
                }),
              },
              end: interval.end,
              remainingSpaces: interval.remainingSpaces,
              start: interval.start,
            },
          };
        }
        return { interval };
      })) ??
    [];

  const getMoreAvailability = async (options: GetMoreAvailabilityOpts) => {
    const { monthOffset, setMonthOffset } = options;

    const ranges = [...new Array(9).keys()].map((count) => {
      const baseDate = addMonths(today, count + monthOffset);

      const rangeStart = set(baseDate, {
        date: 1,
        hours: 0,
        minutes: 0,
        seconds: 0,
      });

      const rangeEnd = set(addMonths(baseDate, 1), {
        date: 1,
        hours: 0,
        minutes: 0,
        seconds: 0,
      });

      return {
        end: toZonedISOString(rangeEnd, timezone),
        start: toZonedISOString(rangeStart, timezone),
      };
    });

    const fetchMoreVariables: GetOrganisationAvailabilityIntervalsQueryVariables =
      {
        ...variables,
        ranges: ranges,
        requestRangesIndividually: true,
      };

    const organisationData = await fetchMore({
      variables: fetchMoreVariables,
    });

    setMonthOffset(monthOffset + 9);

    if (organisationData.data.organisation?.availabilityIntervals) {
      return {
        intervalCount:
          organisationData.data.organisation?.availabilityIntervals.length,
      };
    }

    return { intervalCount: 0 };
  };

  return {
    error,
    getMoreAvailability,
    loading,
    timeSlots,
  };
};
